import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * DateFormatter function signature.
 * @returns a formatted datetime string for the val otherwise
 */
// As DateTimeParser is an interface below, also make DateTimeFormatter an interface as symmetric and for future expandability

export class DateTimeFormatterDelegator {
  /**
   * DateFormatter function signature.
   * @returns implementations typically returns empty string if val is null or undefined,
   * and a formatted datetime string for the val otherwise
   */
  constructor(format) {
    this.format = format;
  }
}

/**
 * @deprecated
 * Kendo contract for a date formatter, which also accepts null | undefined as an arg and returns empty string
 */

// Note: Luxon has subtle difference between format and parse grammars for valid reasons (mostly around offset to timezone ambiguity)!
// Kendo, and any other datetime libs, will have the same limitation as based on spec ambiguity.
// Basically can't parse if it includes timezone info (e.g., EDT, Eastern Daylight Time) as ambiguous if don't also
// know zone (e.g., America/New York) which would make timezone info superfluous.  Note multiple zones have 'EDT' timezones...
// See Limitations for why different: https://moment.github.io/luxon/#/parsing?id=limitations
//
// format grammar: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
// parse grammar: https://moment.github.io/luxon/#/parsing?id=table-of-tokens
// noinspection JSUnusedGlobalSymbols

/**
 * DateParser function signature.
 * @returns implementations typically returns null if val is empty string, null, or undefined,
 * and the parsed Date instance otherwise.
 * @throws It will throw a parse exception if is unable to parse the non-empty val string.
 */

// export type DateParser = (val: string | null | undefined) => Date | null;

export function isDateTimeParser(dtp) {
  return dtp?.parseContext !== undefined && dtp.parse !== undefined;
}
export class DateTimeFormatterAndParser {
  constructor(formatter, parser) {
    _defineProperty(this, "format", void 0);
    _defineProperty(this, "parseContext", void 0);
    _defineProperty(this, "parse", void 0);
    this.format = formatter.format.bind(formatter);
    this.parse = parser.parse.bind(parser);
    this.parseContext = parser.parseContext;
  }
}

/**
 * Encapsulating reasoning about a parseFormat for the particular grammar.
 */

export class DateTimeParseContextImpl {
  constructor(parseGrammarName, parseFormat) {
    this.parseGrammarName = parseGrammarName;
    this.parseFormat = parseFormat;
    // defensive check
    if (!parseFormat) {
      throw new Error("Invalid parse format, can not be null, undefined, or empty string.");
    }
  }
}

/*
  Pete points out only documented subset for Report Builder is in:
      https://docs-stage.abinitio.com/r/416/Metadata_Hub/Metadata_Hub_Reporters_Guide/PART_TWO_Creating_reports/Inserting_and_changing_columns/Changing_column_grouping_name_appearance_and_formatting_of_special_values/Formatting_columns_containing_datetime_values
  That subset of kendo tokens should be easy to convert to the equivalent luxon tokens

  Started down the road of automatically converting kendo formats to luxon and Intl formats,
  but in the end didn't think it was needed for now.  Will probably be time-consuming to support all corner cases,
  and it some conversions might not be possible.  Below is the start of this untested effort...

// aka Kendo 'Default Date Formats" that are localized 'shortcuts' for localized formatting.  All are 1-character
const kendoToLexonLocalizedFormat: { [kendoF: string]: string } = {
  // Same: d, dd, M, MM, MMM, MMMM
  "d": "D",      // en-us: M/d/yyyy
  "D": "DDDD",   // en-us: dddd, MMMM dd, yyyy   -> Monday, November 06, 2000
  "F": "FFFF",    // en-US dddd, MMMM dd, yyyy h: MM: ss tt ->  Monday, November 06, 2000 12:00:00 AM
  "g": "f",    // en-US 11/6/2000 12:00 AM
  "G": "F",    // en-US  11/6/2000 12:00:00 AM

  // time
  // same: t,
  "T": "tt",

  // Luxon does NOT have format string consistent with below kendo format strings (seems like they would be rarely, if ever, used by ab initio customers)
  "M": "",    // kendo 'MMMM dd' (same as m): November 06              // custom format?
  "m": "",    // kendo 'MMMM dd' (same as M): November 06              // custom format?
  "Y": "",     // kendo (same as y): MMMM, yyyy =>  November, 2000     // custom format?
  "y": "",     // kendo (same as Y): MMMM, yyyy =>  November, 2000     // custom format?
  "s": "",     // kendo local sortable value: 2000-11-06 00:00:00      // custom format?:  yyyy-MM-dd HH:mm:ss"
  "u": "",     // kendo local sortable UTC =>   2000-11-06 00:00:00Z   // luxon: no way to specify format output in utc (aka 'Z' timezone)
}

// aka Kendo 'Custom Date Formats'  where user specifies entire format
const kendoToLexonFormat: { [kendoF: string]: string } = {
  // Same: d, dd, M, MM, MMM, MMMM, , yy, yyyy,
  "dddd": "EEEE",    // Wednesday
  "ddd": "EEE",      // Wed:  localized day of week abbrev

  // time
  // Same: h, hh, H, HH, m, mm, s, ss
  "fff": "u",      // 054: num msec, padded to 3
  "ff": "uu",      // 05: hundredths of a sec
  "f": "uuu",      // 0: tenths of a sec
  "tt": "m",      // AM/PM (aka meridiem)
  "zzz": "ZZZZZ"   // unabbreviated time zone "FLE Daylight Time"
}
*/
export let AbInitioCustomFormat = /*#__PURE__*/function (AbInitioCustomFormat) {
  // Localized formats, these were all originally Kendo Formats (but we no longer using Kendo)
  AbInitioCustomFormat["DATE_SHORT"] = "d";
  AbInitioCustomFormat["TIME_WITH_MINUTES"] = "t";
  AbInitioCustomFormat["TIME_WITH_SECONDS"] = "T";
  AbInitioCustomFormat["DATETIME_WITH_MINUTES"] = "g";
  AbInitioCustomFormat["DATETIME_WITH_SECONDS"] = "G";
  AbInitioCustomFormat["DATE_LONG"] = "D";
  AbInitioCustomFormat["DATETIME_LONG"] = "F";
  // Ab Initio custom formats
  AbInitioCustomFormat["MONTH_AND_DAY"] = "ab:month_and_day";
  AbInitioCustomFormat["DATE_RELATIVE"] = "ab:relative_date";
  AbInitioCustomFormat["DATETIME_RELATIVE"] = "ab:relative_datetime";
  AbInitioCustomFormat["DATETIME_RELATIVE_EX"] = "ab:relative_datetime_ex";
  return AbInitioCustomFormat;
}({});

// Note that KendoDateFormat and AbInitioDateFormat types below are structurally equivalent,
// so use different DateFormat types solely for documentation clarity.

/**
 * If string value matches an AbInitioCustomFormat enum value, it will use the corresponding formatter.
 * Otherwise, it treats the string as a Luxon format string and builds a corresponding formatter.
 * @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */

/**
 * @deprecated
 * If string value matches an AbInitioCustomFormat enum value, it will use the corresponding formatter.
 * Otherwise, it treats the string as a Kendo format string and builds a corresponding formatter.
 * @see https://www.telerik.com/kendo-jquery-ui/documentation/globalization/intl/dateformatting#custom-date-formats
 */
//# sourceMappingURL=DateTimeStructs.js.map
